/* eslint-disable react/sort-comp */
/* eslint-disable prettier/prettier */
import React, { createRef } from "react";
import PropTypes from "prop-types";
import mapboxgl from "mapbox-gl"; // or "const mapboxgl = require('mapbox-gl');"
import turfBbox from "@turf/bbox";
import {buildMatchExpression} from "../../utils/mapboxHelper";
import { PANEL_SIDEVIEW_BREAKPOINT, DEFAULT_BBOX_PADDING_SIDEPANEL, DEFAULT_BBOX_PADDING, DEFAULT_PANEL_SML_SCREEN_MIN_HEIGHT } from "../../utils/constants";
import { DataContext } from "../../context/DataContext";



class MapboxMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mapIsLoaded: false,
      selectedCounty: null,
      hoveredCounty: null,
    };
    this.refMap = createRef();
  }

  static contextType = DataContext;


  componentDidMount() {
    const { colorLegendDims, changeSelection, geoJoinKey, choroplethJoinKey, geoData, choroplethData, choroplethColorKey, colorScale } = this.props;
    this.mapBbox = turfBbox(geoData);
    this.map = new mapboxgl.Map({
      container: this.refMap.current,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      bounds: this.mapBbox, // starting position [lng, lat]
      fitBoundsOptions: { padding: {...DEFAULT_BBOX_PADDING, top: colorLegendDims.height + DEFAULT_BBOX_PADDING.top} },
      zoom: 6, // starting zoom
    });
    this.map.once("load", () => {
      this.map.addSource("pa", {
        type: "geojson",
        promoteId: "NAME",
        data: this.props.geoData,
      });
      const matchExpression = buildMatchExpression({data: choroplethData, geoJoinKey, dataJoinKey: choroplethJoinKey, dataAccessor: choroplethColorKey, colorScale})
      this.map.addLayer(
        {
          id: "pa",
          type: "fill",
          source: "pa",
          layout: {},
          paint: {
            'fill-color': matchExpression,
            }
        },
        "watername_ocean"
      );
      this.map.addLayer(
        {
          id: "pa-line",
          type: "line",
          source: "pa",
          layout: {},
          paint: {
            "line-color": [
              "case",
              ["boolean", ["feature-state", "highlight"], false],
              "#191919",
              "white",
            ],
            "line-width": [
              "case",
              ["boolean", ["feature-state", "highlight"], false],
              4,
              0.6,
            ],
          },
        },
        "watername_ocean"
      );
      this.map.setPaintProperty("watername_ocean", "text-color", "#ff0000");

      // HANDLE COUNTY MOUSE ON
      this.map.on('mousemove', 'pa', (e) => {
        const {selectedCounty, hoveredCounty} = this.state
        // only allow hover effect if no county is currently selected (ie. selected county is null)
        if (e.features.length > 0 && !selectedCounty) {
          const newSelectedId = e.features[0].id;
          this.highlightCounty({currentlySelectedId: hoveredCounty, newSelectedId })
          this.setState({
            hoveredCounty: newSelectedId,
          })
        }
        });
      
        this.map.on('mouseleave', 'pa', () => {
          const {selectedCounty, hoveredCounty} = this.state
          if (hoveredCounty && !selectedCounty) {
            const newSelectedId = null

            this.highlightCounty({currentlySelectedId: hoveredCounty, newSelectedId })
            this.setState({
              hoveredCounty: newSelectedId,
            })
          }
          });

      

      // HANDLE COUNTY CLICK
      this.map.on("click", "pa", (e) => {

        if (e.features.length > 0) {
          const featuredId = e.features[0].id;
          changeSelection({
            type: "county",
            id: featuredId,
          });
          this.setState({
            hoveredCounty: null,
            selectedCounty: featuredId,
          });
        }
      });

    // MAP LOADED
    this.setState({
      mapIsLoaded: true,
    })
    });
  }

  componentDidUpdate(prevProps) {
    const {mapIsLoaded} = this.state;
    const { panelDims, selection, choroplethData, geoJoinKey, choroplethJoinKey, choroplethColorKey, colorScale, colorLegendDims } = this.props;

    if (colorLegendDims !== prevProps.colorLegendDims && !mapIsLoaded) {
      this.zoomToFullMap()
    }

    if (!mapIsLoaded) {
      return;
    }

    if (selection.id && panelDims !== prevProps.panelDims) {
      this.zoomToLocation(selection);
    }
  
    if (prevProps.selection.id !== selection.id) {
      this.zoomToLocation(selection);
    }

    if (choroplethData !== prevProps.choroplethData || choroplethColorKey !==prevProps.choroplethColorKey)
   {
      const matchExpression = buildMatchExpression({data: choroplethData, geoJoinKey, dataJoinKey: choroplethJoinKey, dataAccessor: choroplethColorKey, colorScale})

      this.map.setPaintProperty('pa', "fill-color",matchExpression)
    }
  }

  highlightCounty = ({currentlySelectedId, newSelectedId}) => {
    // if this isn't the first click, set prev selected county to false
    if (currentlySelectedId) {
      this.map.setFeatureState(
        { source: "pa", id: currentlySelectedId },
        { highlight: false }
      );
    }
    // set new county
    this.map.setFeatureState(
      { source: "pa", id: newSelectedId },
      { highlight: true }
    );
  };

  clearAllHighlights = () => {
    const features = this.map.querySourceFeatures('pa')
    features.forEach(({id}) => {
      this.map.setFeatureState(
        {source: "pa", id},
        { highlight: false}
      )
    })

  }

  zoomToFullMap = () => {
    const { colorLegendDims } = this.props;
    this.clearAllHighlights() // hackish: fixes bug where sometimes features are left highlighted
    this.map.fitBounds(this.mapBbox, { padding: {...DEFAULT_BBOX_PADDING, top: colorLegendDims.height + DEFAULT_BBOX_PADDING.top}, pitch: 0 });
    this.setState({
      selectedCounty: null,
      hoveredCounty: null,
    });
  };

  zoomToLocation = (selection) => {
    const { geoData, handleShowPanel, handleShowLegend, panelDims } = this.props;
    const { id, county, type } = selection;
    const { mapDims } = this.context;
    const { width: mapWidth } = mapDims;
    if (!id || id === "all") {
      this.zoomToFullMap();
      handleShowPanel(false);
      handleShowLegend(true)
    } else {
      const cleanId = type === "judge" ? county : id;
      const { selectedCounty } = this.state;
      this.highlightCounty({currentlySelectedId: selectedCounty, newSelectedId: cleanId});
      this.setState({
        selectedCounty: cleanId,
      })
      const selectedFeature = geoData.features.filter(
        (feature) =>
          feature.properties.NAME.toLowerCase() === county.toLowerCase()
      );
      const bbox = turfBbox({
        type: "FeatureCollection",
        features: selectedFeature,
      });

      // change padding based on panel size/location
      let boundSettings;
      if (mapWidth > PANEL_SIDEVIEW_BREAKPOINT) {
        boundSettings = {pitch: 0, maxZoom: 7, padding: { ...DEFAULT_BBOX_PADDING_SIDEPANEL, right: panelDims.width + DEFAULT_BBOX_PADDING_SIDEPANEL.right }};
        this.map.fitBounds(bbox, {
          ...boundSettings
        });
      } else {
        boundSettings = {pitch: 0, maxZoom: 6, padding: { ...DEFAULT_BBOX_PADDING, bottom: DEFAULT_PANEL_SML_SCREEN_MIN_HEIGHT + DEFAULT_BBOX_PADDING.bottom}}
        this.map.fitBounds(bbox, {
          ...boundSettings
        });
      }
      // handle overlay items
      handleShowLegend(false)
      handleShowPanel(true)
    }
  };

  render() {

    return (
          <div className="mapbox-map__container-outer">
            <div className="mapbox-map__container-inner" ref={this.refMap} />
          </div>
    );
  }
}

MapboxMap.propTypes = {
  geoData: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.any),
  }).isRequired,
  changeSelection: PropTypes.func.isRequired,
  choroplethJoinKey: PropTypes.string.isRequired,
  choroplethColorKey: PropTypes.string.isRequired,
  geoJoinKey: PropTypes.string.isRequired,
  selection: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    county: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  handleShowPanel: PropTypes.func.isRequired,
  handleShowLegend: PropTypes.func.isRequired,
  choroplethData: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  colorScale: PropTypes.func.isRequired,
  colorLegendDims: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }).isRequired,
  panelDims: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }).isRequired,
};


export default MapboxMap;
